import { Link, useParams } from "react-router-dom";
import { getActorById } from "@/mocks/actors";
import { dramas } from "@/mocks/dramas";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/feature/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";

export default function ActorPage() {
  const { id } = useParams<{ id: string }>();
  const actor = id ? getActorById(id) : undefined;

  usePageMeta(
    actor ? `${actor.name} - 演员资料与参演作品 - 善良的秘书` : "演员信息暂缺 - 善良的秘书",
    actor
      ? `${actor.name}，${actor.role}，代表作包括${actor.works.length}部剧集。查看演员个人简介与参演作品列表。`
      : "该演员信息暂缺，返回首页浏览更多剧集与演员资料。"
  );

  if (!actor) {
    return (
      <div className="w-full px-4 md:px-6 py-16">
        <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "演员详情" }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-user-unfollow-line"
            title="演员信息暂缺"
            description="没有找到这位演员的资料，可能链接有误。你可以返回首页继续浏览。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const works = actor.works
    .map((wid) => dramas.find((d) => d.id === wid))
    .filter((d): d is (typeof dramas)[number] => Boolean(d));

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb
        crumbs={[
          { label: "首页", to: "/" },
          { label: "演员资料" },
          { label: actor.name },
        ]}
      />

      <section className="mt-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-10">
        <div>
          <div className="relative w-48 sm:w-64 lg:w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img
              src={actor.avatar}
              alt={`${actor.name} 演员头像`}
              title={`${actor.name} 高清头像`}
              className="w-full object-cover object-top"
              style={{ aspectRatio: "1 / 1" }}
            />
          </div>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
            {actor.name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs text-accent-900">{actor.role}</span>
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
              生日 {actor.birthday}
            </span>
            <span className="rounded-md bg-primary-50 px-2.5 py-1 text-xs text-primary-700">
              参演 {works.length} 部作品
            </span>
          </div>

          <div className="mt-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">个人简介</h2>
            <p className="mt-2 text-sm md:text-base leading-relaxed text-foreground-700">{actor.bio}</p>
          </div>

          <div className="mt-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">代表作品简介</h2>
            <p className="mt-2 text-sm md:text-base leading-relaxed text-foreground-700">
              代表作品涵盖
              {works.slice(0, 3).map((w, i) => (
                <span key={w.id}>
                  {i > 0 ? "、" : ""}
                  《{w.title}》
                </span>
              ))}
              等剧集。其表演以细节见长，善于在克制中传递情绪的层次，是近年备受观众关注的实力派演员。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-10 md:mt-14">
        <SectionHeading title="参演剧集" subtitle="点击剧集卡片进入详情页" />
        {works.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {works.map((d) => (
              <DramaCard key={d.id} drama={d} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="ri-movie-line"
            title="暂无参演剧集"
            description="这位演员的作品列表暂时还没有收录。"
          />
        )}
      </section>
    </div>
  );
}